"use client";

import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

import { printerCommandFailureMessage } from "../printer-command-failure";
import { printersApi } from "../printers-api";
import type { PrinterRecord } from "../printers-types";
import { ipSchema } from "../validation";

const schema = z.object({ ip: ipSchema });

export function EditNetworkAddressDialog({
	open,
	onOpenChange,
	tenant,
	hostname,
	printer,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenant: string;
	hostname: string;
	printer: PrinterRecord;
}) {
	const queryClient = useQueryClient();
	const network =
		printer.connection.type === "network" ? printer.connection : undefined;

	const update = useMutation(
		printersApi.updateNetworkAddressOptions(
			tenant,
			hostname,
			printer.printerId,
		),
	);

	const form = useAppForm({
		defaultValues: { ip: network?.ip ?? "" } as { ip: string },
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: schema },
		onSubmit: async ({ value }) => {
			const result = await update.mutateAsync({ ip: value.ip.trim() });
			if (result.success) {
				toast.success("Printer address updated.");
				await queryClient.invalidateQueries({
					queryKey: [tenant, "print-proxies", hostname, "printers"],
				});
				onOpenChange(false);
			} else {
				toast.error(
					printerCommandFailureMessage(
						result.code,
						"Failed to update address.",
					),
				);
			}
		},
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit network address</DialogTitle>
					<DialogDescription>
						Update where this printer is reached on the network.
					</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					{update.isError && (
						<Alert variant="destructive">
							<AlertCircle className="size-4" />
							<AlertDescription>
								Unable to update the address. Please try again.
							</AlertDescription>
						</Alert>
					)}

					{/* Printer identity is read-only on edit (AC6) — only the address changes. */}
					<div className="space-y-1">
						<Label className="text-muted-foreground text-xs">Printer</Label>
						<Input value={printer.label} disabled readOnly />
					</div>

					<form.AppField name="ip">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>IP address</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										inputMode="decimal"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<DialogFooter>
						<form.Subscribe selector={(state) => [state.canSubmit]}>
							{([canSubmit]) => (
								<>
									<Button
										type="button"
										variant="outline"
										onClick={() => onOpenChange(false)}
										disabled={update.isPending}
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={!canSubmit || update.isPending}
									>
										{update.isPending && (
											<Loader2 className="mr-2 size-4 animate-spin" />
										)}
										Save
									</Button>
								</>
							)}
						</form.Subscribe>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
